import { useCallback, useMemo } from 'react';
import filter from 'lodash/filter';

import { useTranslation } from '@lyearn/i18n';
import * as ContentListModal from '@/components/ContentListModal';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import Loader from '@/components/Loader';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';
import { ILearningContent } from '@/modules/corporate/components/LearningContent/types';
import useContents from '@/modules/corporate/hooks/useContents';
import useContentsInCollection from '@/modules/corporate/hooks/useContentsInCollection';
import { ALL_CONTENT_TYPES } from '@/modules/corporate/modules/assignment/views/AssignmentCreator/components/ContentAndCollectionList/utils';
import { OrderEnum, ResourceTypeEnum } from '@/types/schema';

import { adaptContent } from '../../helpers';
import ContentSelector from '../ContentSelector';
import EmptyState from './EmptyState';

const DEFAULT_SORT = [
  {
    field: 'relevance',
    order: OrderEnum.Desc,
  },
];

interface SelectContentsDialogProps {
  onClose: () => void;
  currentSectionContentIds: string[];
  alreadySelectedIds?: string[];
  hideAlreadySelectedIds?: boolean;
  onAddContent: (childResourceIds: string[], contents: ILearningContent[]) => void;
  title?: string;
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  searchPlaceholder?: string;
  contentTypes?: ResourceTypeEnum[];
  fetching?: boolean;
  collectionId?: string;
  multiSelect?: boolean;
  excludeReceivedContents?: boolean;
  shareable?: boolean;
}

function SelectContentsDialog({
  onClose,
  currentSectionContentIds,
  alreadySelectedIds,
  hideAlreadySelectedIds = false,
  onAddContent,
  title,
  description,
  primaryButtonText,
  secondaryButtonText,
  searchPlaceholder,
  multiSelect = true,
  contentTypes = ALL_CONTENT_TYPES,
  fetching,
  collectionId,
  excludeReceivedContents,
  shareable,
}: SelectContentsDialogProps) {
  const { t } = useTranslation('Path');
  const [searchStr, setSearchStr, debounceSearchStr] = useDebounceState('');
  const {
    contents,
    isFetching: initialFetching,
    fetchMore,
  } = useContents({
    searchString: debounceSearchStr,
    types: contentTypes,
    pause: !!collectionId,
    idsToExclude: hideAlreadySelectedIds ? alreadySelectedIds : [],
    excludeReceivedContents,
    shareable,
    sort: DEFAULT_SORT,
  });

  const filterManager = useFilterManagerWithState({ defaultFilters: { types: contentTypes } });

  const {
    contents: contentsInCollection,
    initialFetching: initialFetchingContentsInCollection,
    fetchMore: fetchMoreContentsInCollection,
  } = useContentsInCollection({
    searchString: debounceSearchStr,
    state: filterManager.state,
    collectionId: collectionId!,
    shareable,
    pause: !collectionId,
  });

  const resolvedData = !collectionId
    ? {
        contents,
        initialFetching,
        fetchMore,
      }
    : {
        contents: contentsInCollection,
        initialFetching: initialFetchingContentsInCollection,
        fetchMore: fetchMoreContentsInCollection,
      };
  const { selectedItemsSet, has, toggle } = useEntitySet(currentSectionContentIds);

  const adaptedContents = useMemo(
    () => resolvedData.contents?.map((content) => adaptContent(t, content)),
    [resolvedData.contents, t],
  );

  const onClickSingleSelect = useCallback(
    (contentId: string) => {
      selectedItemsSet.clear();
      toggle(contentId);
    },
    [selectedItemsSet, toggle],
  );

  const onAddClick = useCallback(() => {
    onAddContent(
      Array.from(selectedItemsSet),
      filter(adaptedContents, (content) => has(content.id)),
    );
  }, [adaptedContents, has, onAddContent, selectedItemsSet]);

  return (
    <ContentListModal.Main
      action={{
        discardText: secondaryButtonText ?? t('Common:Cancel'),
        onSubmit: onAddClick,
        text: primaryButtonText ?? t('Path:AddContent'),
        disabled: !selectedItemsSet.size || fetching,
      }}
      fetchMore={resolvedData.fetchMore}
      header={{
        title: title ?? t('Path:SelectLearningContent'),
        description: description ?? t('Path:SelectLearningContentDescription'),
      }}
      items={adaptedContents}
      searchBar={{
        value: searchStr,
        onChange: setSearchStr,
        selectedCount: multiSelect ? selectedItemsSet.size : undefined,
        placeholder: searchPlaceholder ?? t('Path:SearchForLearningContent'),
      }}
      onClose={onClose}>
      {adaptedContents?.length ? (
        <ContentListModal.Body
          className="flex-1"
          itemRenderer={(index: number) => {
            const currentContent = adaptedContents[index];

            if (!currentContent) {
              return null;
            }

            const { id } = currentContent;
            const isAlreadySelected = collectionId
              ? currentContent.isInGroup
              : !!alreadySelectedIds?.includes(id);

            return (
              <ContentSelector
                key={id}
                content={currentContent}
                isAlreadySelected={isAlreadySelected}
                isSelected={selectedItemsSet.has(id)}
                multiSelect={multiSelect}
                onClick={multiSelect ? () => toggle(id) : () => onClickSingleSelect(id)}
              />
            );
          }}
        />
      ) : resolvedData.initialFetching ? (
        <Loader />
      ) : (
        <EmptyState searchString={debounceSearchStr} />
      )}
    </ContentListModal.Main>
  );
}

export default SelectContentsDialog;
